import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ErrorApi, guardarCredenciales } from '../api/cliente'
import { comprobarConexion } from '../api/consultas'
import { pedirAlmacenamientoPersistente } from '../lib/almacen'
import { estaActivado } from '../lib/biometria'
import Bloqueo from './Bloqueo'

/**
 * Puerta de entrada a la app.
 *
 * En desarrollo el proxy de Vite inyecta la autenticacion y esto no llega a
 * verse nunca. En produccion la PWA se sirve desde el VPS sin proxy, asi que
 * las credenciales tienen que estar en el dispositivo.
 */
export default function Sesion({ children }: { children: ReactNode }) {
  const clienteConsultas = useQueryClient()
  const [usuario, setUsuario] = useState('')
  const [clave, setClave] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)
  const [bloqueada, setBloqueada] = useState(estaActivado)

  const { data, error, isLoading } = useQuery({
    queryKey: ['conexion'],
    queryFn: comprobarConexion,
    retry: false,
  })

  async function entrar(e: React.FormEvent) {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)

    guardarCredenciales({ usuario: usuario.trim(), clave })
    try {
      await comprobarConexion()
      // Se pide aqui y no al arrancar porque Safari solo concede la
      // persistencia tras una accion del usuario. Sin ella iOS puede borrar
      // las credenciales y las descargas cuando le falte espacio.
      await pedirAlmacenamientoPersistente()
      await clienteConsultas.invalidateQueries()
    } catch (err) {
      guardarCredenciales(null)
      setFallo(err instanceof ErrorApi ? err.message : 'No se pudo conectar')
    } finally {
      setEnviando(false)
    }
  }

  // Antes que nada, y tambien antes de saber si hay servidor: el bloqueo
  // tiene que valer sin conexion, que es cuando mas sentido tiene.
  if (bloqueada) return <Bloqueo alAbrir={() => setBloqueada(false)} />

  if (isLoading) {
    return <div className="acceso centrado">Conectando…</div>
  }

  if (data) return <>{children}</>

  const esAuth = error instanceof ErrorApi && error.esAutenticacion

  // Si la peticion no llego a salir se entra igual, haya credenciales o no.
  //
  // Antes esto cortaba el paso y lo descargado en el telefono quedaba
  // inalcanzable justo cuando mas falta hace: la app no llegaba a montarse y
  // no habia forma de abrir Descargas. Pedir la contrasena tampoco servia de
  // nada, porque comprobarla necesita la misma red que falta.
  //
  // Lo que distingue este caso es que `estado` viene vacio: hubo un fallo de
  // red, no una respuesta del servidor. Un 401 sigue llevando al formulario y
  // un 500 al aviso de error, que ahi si hay algo que contar.
  const sinRed = error instanceof ErrorApi && !esAuth && error.estado === undefined
  if (sinRed) return <>{children}</>

  return (
    <div className="acceso">
      <img src="/icons/marca.png" alt="Yomou" className="marca-acceso" />
      <h1>Yomou</h1>

      {esAuth || fallo ? (
        <form onSubmit={entrar}>
          <p className="subtitulo">Entra con tu usuario del servidor</p>

          {/*
            `name` y `autoComplete` no son decoracion: sin ellos iOS no
            reconoce esto como un formulario de acceso, no ofrece guardar la
            contrasena en el llavero y luego no hay nada que rellenar con
            Face ID. Es la unica via que sobrevive a que iOS borre los datos
            del sitio, porque el llavero no se toca.
          */}
          <label htmlFor="usuario">Usuario</label>
          <input
            id="usuario"
            name="usuario"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          />

          <label htmlFor="clave">Contrasena</label>
          <input
            id="clave"
            name="clave"
            type="password"
            autoComplete="current-password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />

          {fallo && <p className="error-texto">{fallo}</p>}

          <button
            className="boton"
            type="submit"
            disabled={enviando || !usuario || !clave}
            style={{ width: '100%', marginTop: 18 }}
          >
            {enviando ? 'Comprobando…' : 'Entrar'}
          </button>
        </form>
      ) : (
        <div className="aviso error">
          <strong>No se pudo conectar con el servidor</strong>
          <p>{(error as Error)?.message}</p>
          <button className="boton secundario" onClick={() => location.reload()}>
            Reintentar
          </button>
        </div>
      )}
    </div>
  )
}
